import { alpha, Stack, useTheme } from "@mui/material";
import { Banknote, Infinity as InfinityIcon, TrendingUp } from "lucide-react";
import type { FC } from "react";
import { RentPhaseRow } from "web_myplann/Goals/Components/RentPlan/RentPhaseRow";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import type { RentPlan } from "web_myplann/Goals/Types/RentPlan";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type Props = {
	plan: RentPlan;
};

export const RentPhaseDescriptions: FC<Props> = ({ plan }) => {
	const theme = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();
	const accumColor = theme.palette.blue.main;
	const drawColor = theme.palette.orange.main;

	return (
		<Stack gap={1}>
			<RentPhaseRow
				icon={<TrendingUp size={theme.iconSize.sm} color={accumColor} />}
				rowColor={alpha(accumColor, 0.06)}
				chipColor={alpha(accumColor, 0.12)}
				title={t(Resources.Goals.Detail.RentPlan.accumPhaseTitle, {
					year: plan.dueYear,
				})}
				titleSuffix={t(Resources.Goals.Detail.RentPlan.durationYears, {
					count: plan.accumYears,
				})}
				description={t(Resources.Goals.Detail.RentPlan.accumPhaseDescription, {
					amount: formatAmount(plan.savingsTarget, plan.currencyCode),
				})}
			/>
			<RentPhaseRow
				icon={
					plan.isPerpetual ? (
						<InfinityIcon size={theme.iconSize.sm} color={drawColor} />
					) : (
						<Banknote size={theme.iconSize.sm} color={drawColor} />
					)
				}
				rowColor={alpha(drawColor, 0.06)}
				chipColor={alpha(drawColor, 0.12)}
				title={
					plan.isPerpetual
						? t(Resources.Goals.Detail.RentPlan.drawPhaseTitlePerpetual, {
								startYear: plan.dueYear,
							})
						: t(Resources.Goals.Detail.RentPlan.drawPhaseTitleToZero, {
								startYear: plan.dueYear,
								endYear: plan.endYear,
							})
				}
				titleSuffix={
					plan.isPerpetual
						? undefined
						: t(Resources.Goals.Detail.RentPlan.durationYears, {
								count: plan.drawYears,
							})
				}
				description={
					plan.isPerpetual
						? t(Resources.Goals.Detail.RentPlan.drawPhaseDescriptionPerpetual, {
								amount: formatAmount(plan.monthlyRent, plan.currencyCode),
							})
						: t(Resources.Goals.Detail.RentPlan.drawPhaseDescriptionToZero, {
								amount: formatAmount(plan.monthlyRent, plan.currencyCode),
							})
				}
			/>
		</Stack>
	);
};
